import { useState, useEffect, useCallback, FC } from "react"
import { useLocation, Location } from "react-router-dom"

import { Drawer, Box } from "@mui/material"

import { useLicenseStore } from "../../../store"
import { Persona } from "../../../types/Custom/Types"
import SynapseLogo from "../assets/synapseLogo.svg?react"
import { getActiveScreen, isMultipleBranchesMode, splitPersonaAndScreen } from "../helpers"
import UserData from "./UserData"

import styles from "./Sidebar.module.scss"

interface Props {
  availablePersonas?: {
    hasAnalytics: boolean
    hasAdmin: boolean
    hasMarketing: boolean
    hasSecurity: boolean
  }
  persona: Persona
  setPersona: (persona: Persona) => void
  handleToggleDrawer: () => void
  handleCloseDrawer: () => void
  isDrawerOpen: boolean
}
const Sidebar: FC<Props> = ({ handleCloseDrawer, handleToggleDrawer, persona, setPersona, isDrawerOpen }) => {
  const location = useLocation()
  const [isWarningDisplayed] = useLicenseStore((state) => [state.isWarningDisplayed ?? false])
  const isMultipleBranchesRoute = isMultipleBranchesMode(location.pathname)

  // capture current active screen from url pathname
  const getInitialActiveScreen = () => {
    const screenRoute = location?.pathname.split("/").slice(-1).toString() ?? ""
    const personaAndScreen = splitPersonaAndScreen(screenRoute)
    const validPersonas: Persona[] = ["analytics", "admin", "marketing", "security"]
    if (isMultipleBranchesRoute && !validPersonas.includes(personaAndScreen.persona)) {
      // Use getActiveScreen to ensure consistency with how SidebarItem checks for active state
      return getActiveScreen(screenRoute)
    }
    return personaAndScreen.screen
  }
  const [activeScreen, setActiveScreen] = useState(getInitialActiveScreen())

  // this function uses all util functions defined before ,to handle every location change
  const handleLocationChange = useCallback(
    (location: Location) => {
      const pagePathName = location?.pathname?.split("/")
      const screenRoute = pagePathName.reverse().find((element) => !Number(element)) ?? ""
      // extract persona and screen route
      const personaAndScreen = splitPersonaAndScreen(screenRoute)
      // If in multiple branches mode and route doesn't have a persona prefix, don't set persona
      const validPersonas: Persona[] = ["analytics", "admin", "marketing", "security"]
      const isMultipleBranches = isMultipleBranchesMode(location.pathname)
      if (isMultipleBranches && !validPersonas.includes(personaAndScreen.persona)) {
        // Don't set persona in multiple mode
        // Use getActiveScreen to ensure consistency with how SidebarItem checks for active state
        setActiveScreen(getActiveScreen(screenRoute))
      } else {
        setPersona(personaAndScreen.persona)
        setActiveScreen(personaAndScreen.screen)
      }
      // close drawer if on mobile after successful navigation.
      handleCloseDrawer()
    },
    [setPersona, handleCloseDrawer]
  )
  // triggered to handle each location change
  useEffect(() => {
    handleLocationChange(location)
  }, [location, handleLocationChange])

  const handleOpenSynapseSite = () => window.open("https://azkavision.synapse-analytics.io/", "_blank")

  const drawer = (
    <div
      className={styles.wrapper}
      style={{
        marginTop: isWarningDisplayed ? "56px" : undefined,
      }}
    >
      {/* User Data */}
      <UserData persona={persona} activeScreen={activeScreen} />

      {/* copyright */}
      <SynapseLogo className={styles.synapseLogo} onClick={handleOpenSynapseSite} />
    </div>
  )

  return (
    <nav className={styles.drawer} aria-label="navigation">
      {/* Desktop navigation */}

      <Box sx={{ display: { lg: "block", xs: "none" }, height: "100%" }}>
        <Drawer
          classes={{
            paper: styles.drawerPaper,
            root: styles.drawerRoot,
          }}
          variant="permanent"
          anchor="left"
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Mobile navigation */}

      <Box sx={{ display: { lg: "none", xs: "block" } }}>
        <Drawer
          variant="temporary"
          open={isDrawerOpen}
          anchor="left"
          onClose={handleToggleDrawer}
          classes={{
            paper: styles.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </nav>
  )
}

export default Sidebar
